import jsPDF from 'jspdf';
import { GenerateVendorInvoicePDFParams } from './types';
import { generateInvoiceFilename } from './invoiceCalculations';
import {
  renderReviewedBox,
  renderNspUseOnlyBox,
} from './invoiceComponents';
import { buildVendorInvoiceDocument } from './vendorInvoicePdfGenerator';

// The NSP version of the invoice displays the due date 10 days earlier than
// the vendor-facing copy, to build in a processing buffer.
const NSP_DUE_DATE_OFFSET_DAYS = 10;

/**
 * Build an NSP invoice document (vendor invoice + reviewed/NSP boxes)
 * This adds the NSP-specific boxes on top of the vendor invoice
 */
export const buildNspInvoiceDocument = async ({
  vendor,
  primaryIssuer,
  monthYear,
  hstRate,
}: GenerateVendorInvoicePDFParams): Promise<jsPDF> => {
  // Create a new document and add NSP boxes at the top
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const leftMargin = 14;
  let yPosition = 15;

  // TOP SECTION - Reviewed and NSP Use Only boxes (side by side, always shown)
  const reviewedHeight = renderReviewedBox(doc, primaryIssuer, leftMargin, yPosition, vendor.invoice_computed_approval_signature, vendor.approved_at);
  const nspUseOnlyHeight = renderNspUseOnlyBox(doc, vendor, pageWidth - 81 - leftMargin, yPosition);
  yPosition += Math.max(reviewedHeight, nspUseOnlyHeight) + 4;

  // Build the rest of the invoice using the vendor invoice builder
  // Pass the existing doc and current Y position to continue from where boxes ended
  return buildVendorInvoiceDocument(
    { vendor, primaryIssuer, monthYear, hstRate },
    doc,
    yPosition,
    NSP_DUE_DATE_OFFSET_DAYS
  );
};

/**
 * Generate a vendor invoice PDF and download it
 * Uses NSP invoice document (with boxes) by default for backward compatibility
 */
export const generateNspInvoicePDF = async (params: GenerateVendorInvoicePDFParams): Promise<void> => {
  const doc = await buildNspInvoiceDocument(params);

  // Generate filename and download
  const filename = generateInvoiceFilename(params.vendor.vendor_name, params.monthYear, 'NSP - ');
  doc.save(filename);
};

/**
 * Build an NSP invoice document and return it as a Blob (for bundling into a ZIP)
 */
export const buildNspInvoiceBlob = async (params: GenerateVendorInvoicePDFParams): Promise<{ blob: Blob; filename: string }> => {
  const doc = await buildNspInvoiceDocument(params);
  const filename = generateInvoiceFilename(params.vendor.vendor_name, params.monthYear, 'NSP - ');
  const blob = doc.output('blob');
  return { blob, filename };
};
